<template>
  <section
    class="doctor-editor"
    :dir="$i18n.locale === 'en' ? 'ltr' : 'rtl'"
  >
    <header><h3>{{ selectionOnly ? (selectionTitle || words.ruleTitle) : words.editorTitle }}</h3><p v-if="selectionOnly || mode === 'manual'">
      {{ selectionOnly ? (selectionIntro || words.ruleIntro) : words.editorIntro }}
    </p><small v-if="!selectionOnly">{{ words.locale }}</small></header>
    <label
      v-if="!selectionOnly"
      class="doctor-mode"
    >{{ words.mode }}<select
      :value="mode"
      @change="changeMode($event.target.value)"
    ><option value="auto">{{ words.auto }}</option><option value="manual">{{ words.manual }}</option><option value="disabled">{{ words.disabled }}</option></select></label>
    <p v-if="!selectionOnly && mode === 'auto'">
      {{ words.autoHint }} <router-link :to="{ name: 'article-doctor-policy' }">
        {{ words.globalSettings }}
      </router-link>
    </p>
    <template v-if="selectionOnly || mode === 'manual'">
      <div class="editor-tools">
        <button
          v-if="!selectionOnly"
          type="button"
          :disabled="loading || !topicIds.length"
          @click="find(false)"
        >
          {{ words.suggest }}
        </button><label><span>{{ words.search }}</span><input
          v-model="search"
          maxlength="60"
          @keydown.enter.prevent="find(true)"
        ></label><button
          type="button"
          :disabled="loading || search.trim().length < 2"
          @click="find(true)"
        >
          {{ words.find }}
        </button>
      </div>
      <p
        v-if="message"
        role="status"
      >
        {{ message }}
      </p>
      <div class="selected-doctors">
        <p v-if="!ids.length">
          {{ words.none }}
        </p><div
          v-for="(id, index) in ids"
          :key="id"
        >
          <bdi dir="auto">{{ selectedName(id) }}</bdi><span
            v-if="orderable"
            class="doctor-order"
          ><button
            type="button"
            :disabled="index === 0"
            :aria-label="`${words.up}: ${selectedName(id)}`"
            @click="move(index, -1)"
          >{{ words.up }}</button><button
            type="button"
            :disabled="index === ids.length - 1"
            :aria-label="`${words.down}: ${selectedName(id)}`"
            @click="move(index, 1)"
          >{{ words.down }}</button></span><button
            type="button"
            :aria-label="removeLabel(id)"
            @click="remove(id)"
          >
            {{ words.remove }}
          </button>
        </div>
      </div>
      <div
        v-if="options.length"
        class="doctor-options"
      >
        <label
          v-for="doctor in options"
          :key="doctor.id"
        ><input
          type="checkbox"
          :checked="ids.includes(doctor.id)"
          :disabled="!ids.includes(doctor.id) && ids.length >= 3"
          @change="toggle(doctor)"
        ><span><strong><bdi dir="auto">{{ doctor.name }}</bdi></strong><small><bdi dir="auto">{{ doctor.speciality }}</bdi></small><small v-if="reason(doctor)">{{ words.reason }} {{ reason(doctor) }}</small></span></label>
      </div>
      <div
        v-if="!selectionOnly"
        class="editor-settings"
      >
        <label><span>{{ words.placement }}</span><select
          :value="placement"
          @change="emit({ doctorBlockPlacement: $event.target.value })"
        ><option
          v-if="!endOnly"
          value="middle"
        >{{ words.middle }}</option><option value="end">{{ words.end }}</option></select></label>
      </div>
    </template>
  </section>
</template>
<script>
import axios from '@/libs/axios'
import messages from './doctor-messages'

export default {
  props: {
    mode: { type: String, default: 'auto' },
    selectionOnly: Boolean,
    orderable: Boolean,
    selectionTitle: { type: String, default: '' },
    selectionIntro: { type: String, default: '' },
    ids: { type: Array, default: () => [] },
    enabled: Boolean,
    endOnly: Boolean,
    placement: { type: String, default: 'middle' },
    tags: { type: Array, default: () => [] },
    lang: { type: String, default: 'fa' },
  },
  data() {
    return {
      search: '', options: [], selected: [], loading: false, sequence: 0, hydrationSequence: 0, loadingProfile: false, selectionLoaded: false, message: '',
    }
  },
  computed: {
    words() { return messages[this.$i18n.locale] || messages.fa },
    topicIds() { return this.tags.map(tag => this.topicId(tag)).filter(id => /^[a-f0-9]{24}$/i.test(id || '')).slice(0, 20) },
  },
  watch: { topicIds() { this.sequence += 1; this.loading = false; this.options = []; this.message = '' }, lang() { this.sequence += 1; this.loading = false; this.options = []; this.selected = []; this.hydrate() }, ids() { this.hydrate() } },
  created() { this.hydrate() },
  beforeDestroy() { this.sequence += 1; this.hydrationSequence += 1 },
  methods: {
    optionRows(response) {
      if (!response.data || !Array.isArray(response.data.data)) throw new Error('INVALID_DOCTOR_OPTIONS')
      return response.data.data.filter(doctor => doctor && typeof doctor.id === 'string' && typeof doctor.name === 'string' && typeof doctor.speciality === 'string').slice(0, 12)
    },
    topicId(tag) { const { _id: id } = tag; return id },
    changeMode(mode) { this.emit({ doctorBlockMode: mode, doctorBlockEnabled: mode === 'manual' }) },
    emit(value) { this.$emit('change', value) },
    remove(id) { const ids = this.ids.filter(value => value !== id); this.emit({ recommendedDoctorIds: ids, doctorBlockEnabled: this.mode === 'manual' && ids.length > 0 }) },
    toggle(doctor) {
      if (this.ids.includes(doctor.id)) { this.remove(doctor.id); return }
      if (this.ids.length >= 3) { this.message = this.words.max; return }
      this.selected = [...this.selected.filter(item => item.id !== doctor.id), doctor]
      this.emit({ recommendedDoctorIds: [...this.ids, doctor.id] })
    },
    move(index, offset) { const next = index + offset; if (!this.orderable || next < 0 || next >= this.ids.length) return; const ids = [...this.ids]; [ids[index], ids[next]] = [ids[next], ids[index]]; this.emit({ recommendedDoctorIds: ids }) },
    removeLabel(id) { return `${this.words.remove}: ${this.selectedName(id)}` },
    selectedName(id) { const doctor = this.selected.find(item => item.id === id); if (doctor) return doctor.name; if (this.loadingProfile) return this.words.loading; if (this.selectionLoaded) return this.words.unavailable; return this.words.failed },
    reason(doctor) { return this.tags.filter(tag => (doctor.matchedTagIds || []).includes(this.topicId(tag))).map(tag => tag.title).join('، ') },
    async hydrate() {
      this.hydrationSequence += 1
      const sequence = this.hydrationSequence
      this.selectionLoaded = false; this.loadingProfile = false
      if (!this.ids.length) { this.selected = []; return }
      this.loadingProfile = true
      try {
        const response = await axios.post('articles/doctor-options', { ids: this.ids }, { headers: { lang: this.lang }, timeout: 10000 })
        if (sequence === this.hydrationSequence) { this.selected = this.optionRows(response); this.selectionLoaded = true }
      } catch (error) { if (sequence === this.hydrationSequence) this.message = this.words.failed } finally { if (sequence === this.hydrationSequence) this.loadingProfile = false }
    },
    async find(manual) {
      if (this.loading || (manual && this.search.trim().length < 2) || (!manual && !this.topicIds.length)) return
      this.sequence += 1
      const { sequence } = this
      this.loading = true; this.message = ''; this.options = []
      try {
        const response = await axios.post('articles/doctor-options', manual ? { search: this.search.trim(), tagIds: this.topicIds } : { tagIds: this.topicIds }, { headers: { lang: this.lang }, timeout: 10000 })
        if (sequence !== this.sequence) return
        this.options = this.optionRows(response)
        if (!this.options.length) this.message = this.words.empty
      } catch (error) { if (sequence === this.sequence) this.message = this.words.failed } finally { if (sequence === this.sequence) this.loading = false }
    },
  },
}
</script>
<style scoped>
.doctor-editor { border: 1px solid #dcebea; background: #f8fbfb; border-radius: 14px; padding: 20px; margin: 20px 0; color: #405056; }
h3 { font: inherit; font-size: 17px; font-weight: 700; margin: 0 0 8px; } header p { font-size: 13px; line-height: 1.9; margin-bottom: 6px; } small { display: block; font-size: 11px; color: #6c8085; line-height: 1.8; }
.editor-tools, .editor-settings { display: flex; align-items: end; flex-wrap: wrap; gap: 12px; margin-top: 16px; }
.editor-tools label { flex: 1; min-width: 180px; } label { font-size: 12px; display: grid; gap: 6px; }
input:not([type='checkbox']), select { border: 1px solid #d9e4e4; border-radius: 8px; background: #fff; width: 100%; padding: 10px; font: inherit; }
button { font: inherit; font-size: 12px; min-height: 44px; border: 1px solid #cce2e1; border-radius: 8px; background: #edf7f6; color: #16736e; padding: 10px 14px; cursor: pointer; }
button:disabled { opacity: .55; cursor: default; } button:focus-visible, input:focus-visible, select:focus-visible { outline: 2px solid #00a19a; outline-offset: 3px; }
.doctor-order { display: flex; gap: 8px; flex-wrap: wrap; }
.selected-doctors { margin: 18px 0; font-size: 12px; } .selected-doctors div { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; padding: 8px 0; border-bottom: 1px solid #e4eded; }
.doctor-options { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; }
.doctor-options label { display: flex; align-items: start; gap: 10px; padding: 14px; background: #fff; border: 1px solid #e0eaea; border-radius: 9px; cursor: pointer; }
input[type='checkbox'] { width: 18px; height: 18px; flex-shrink: 0; accent-color: #00a19a; margin: 3px 0; } .enable-doctors { display: flex; align-items: center; min-height: 44px; gap: 8px; }
@media(max-width:600px) { .doctor-editor { padding: 16px; } .doctor-options { grid-template-columns: 1fr; } .editor-tools label { min-width: 0; flex-basis: 100%; } }
</style>
<style>
.dark-layout .doctor-editor { background: #283046; border-color: #3b4253; color: #d0d2d6; }
.dark-layout .doctor-editor .doctor-options label, .dark-layout .doctor-editor select, .dark-layout .doctor-editor input:not([type='checkbox']) { background: #161d31; color: #d0d2d6; border-color: #3b4253; }
</style>
